// FILE: src/components/leave/LeaveBalanceSummary.jsx
// NEW FILE — place in src/components/leave/

import React, { useState, useEffect } from 'react';
import { leaveAPI } from '../../services/api';

/**
 * Reusable component showing entitled vs. remaining leave balance.
 * Used in Faculty, HOD, Admin, and Finance dashboards.
 */
const LeaveBalanceSummary = ({ userId = null, year = null }) => {
  const [balances, setBalances] = useState([]);
  const [loading, setLoading] = useState(true);
  const currentYear = year || new Date().getFullYear();

  useEffect(() => {
    loadBalances();
  }, [userId, year]);

  const loadBalances = async () => {
    try {
      setLoading(true);
      const data = await leaveAPI.getMyBalances(currentYear);
      const arr = Array.isArray(data) ? data : (data?.results || []);
      setBalances(arr);
    } catch (err) {
      console.error('Error loading leave balances:', err);
    } finally {
      setLoading(false);
    }
  };

  const getBarColor = (remaining, allocated) => {
    if (allocated === 0) return '#6b7280';
    const pct = (remaining / allocated) * 100;
    if (remaining < 0) return '#ef4444';   // red for negative
    if (pct <= 25) return '#f97316';        // orange for low
    if (pct <= 50) return '#eab308';        // yellow for medium
    return '#22c55e';                        // green for healthy
  };

  if (loading) return <div style={{ padding: '1rem', color: '#6b7280' }}>Loading balances…</div>;

  if (balances.length === 0) {
    return (
      <div style={{ padding: '1rem', textAlign: 'center', color: '#6b7280' }}>
        No leave balance data available for {currentYear}.
      </div>
    );
  }

  return (
    <div className="leave-balance-summary">
      <h3 style={{ marginBottom: '1rem', fontSize: '1rem', fontWeight: 600 }}>
        Leave Balance — {currentYear}
      </h3>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
        gap: '0.75rem'
      }}>
        {balances.map((b) => {
          const allocated = parseFloat(b.allocated) || 0;
          const used = parseFloat(b.used) || 0;
          const remaining = parseFloat(b.remaining) || 0;
          const barPct = allocated > 0 ? Math.min(Math.max((remaining / allocated) * 100, 0), 100) : 0;
          const isNegative = remaining < 0;

          return (
            <div key={b.id} style={{
              background: '#fff',
              border: `1px solid ${isNegative ? '#fecaca' : '#e5e7eb'}`,
              borderRadius: '8px',
              padding: '0.75rem',
              backgroundColor: isNegative ? '#fff5f5' : '#fff'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                <span style={{ fontWeight: 600, fontSize: '0.8rem', color: '#374151' }}>
                  {b.leave_type_display}
                </span>
                {isNegative && (
                  <span style={{ fontSize: '0.7rem', color: '#ef4444', fontWeight: 600 }}>
                    NEGATIVE
                  </span>
                )}
              </div>
              {/* Progress bar */}
              <div style={{
                height: '6px',
                background: '#f3f4f6',
                borderRadius: '3px',
                marginBottom: '0.4rem',
                overflow: 'hidden'
              }}>
                <div style={{
                  height: '100%',
                  width: `${barPct}%`,
                  background: getBarColor(remaining, allocated),
                  borderRadius: '3px',
                  transition: 'width 0.3s'
                }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#6b7280' }}>
                <span>
                  <strong style={{ color: isNegative ? '#ef4444' : '#111827' }}>
                    {remaining < 0 ? remaining.toFixed(1) : remaining.toFixed(1)}
                  </strong> remaining
                </span>
                <span>{allocated.toFixed(1)} entitled</span>
              </div>
              <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginTop: '0.2rem' }}>
                Used: {used.toFixed(1)} | {b.is_paid ? '💰 Paid' : '🚫 Unpaid'}
                {b.allow_negative && <span style={{ marginLeft: '4px' }}>• LOP allowed</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default LeaveBalanceSummary;